import classnames from "classnames";
import { noop } from "lodash";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { BlockInViewOnlyMode } from "@circle-react/components/ViewOnlyMasquerading/BlockInViewOnlyMode";
import { useIsOrCanBeCommunityMember } from "@circle-react/contexts";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { CommentButton } from "./CommentButton";
import { LikeButton } from "./LikeButton";

// TODO: Refactor the actions out, we are going to just always refetch the post so don't have to do any complex logic.
export interface EngagementActionsProps {
  commentButtonClassName?: string;
  commentIconClassName?: any;
  likeButtonClassName?: string;
  likedSpanClassName?: string;
  likeIconClassName?: string;
  onCommentsClick?: (event?: React.MouseEvent) => void;
  showCommentsCount?: boolean;
  showLikesCount?: boolean;
  type?: "list" | "card" | "image-card";
  wrapperClassName?: string;
}

export const EngagementActions = ({
  type = "list",
  wrapperClassName,
  commentButtonClassName,
  commentIconClassName,
  likeButtonClassName,
  likeIconClassName,
  likedSpanClassName,
  showCommentsCount = true,
  showLikesCount = true,
  onCommentsClick = noop,
}: EngagementActionsProps) => {
  const {
    post,
    showEngagementActions: shouldShowEngagementActions,
    onLikeToggle,
  } = useCurrentPostHelpers();

  const {
    user_likes_count,
    is_liking_enabled,
    is_comments_enabled,
    is_liked_by_current_user,
    comments_count,
    post_details_loaded,
    display_title,
  } = post;

  const isOrCanBeCommunityMember = useIsOrCanBeCommunityMember();

  if (
    !shouldShowEngagementActions ||
    (!is_liking_enabled && !is_comments_enabled)
  ) {
    return null;
  }

  if (!post_details_loaded) {
    return (
      <div
        className={classnames(
          `post--${type}__actions post--${type}__actions-visible`,
          {
            "flex items-center": type === "list",
          },
          {
            "absolute bottom-4 left-4 flex items-center gap-2": type === "card",
          },
          wrapperClassName,
        )}
      >
        <div
          className={classnames(
            "action-item action-link action-like post__actions--like p-1",
            likeButtonClassName,
          )}
        >
          <span className="action__icon">
            <SkeletonLoader variant="row" lg short />
          </span>
        </div>
        <div
          className={classnames(
            "action-item action-comment p-1",
            commentButtonClassName,
          )}
        >
          <span className="action__icon">
            <SkeletonLoader variant="row" lg short />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        `post--${type}__actions post--${type}__actions-visible`,
        {
          "grid grid-cols-2 items-center text-sm md:w-40": type === "list",
        },
        {
          "absolute bottom-4 left-4 flex items-center gap-2": type === "card",
        },
        {
          "flex items-center gap-2 text-sm": type === "image-card",
        },
        wrapperClassName,
      )}
    >
      <BlockInViewOnlyMode>
        {is_liking_enabled && (
          <LikeButton
            name={display_title}
            onLikeToggle={event => void onLikeToggle?.(event)}
            isOrCanBeCommunityMember={isOrCanBeCommunityMember}
            user_likes_count={user_likes_count}
            is_liked_by_current_user={is_liked_by_current_user}
            className={likeButtonClassName}
            iconClassName={likeIconClassName}
            likedSpanClassName={likedSpanClassName}
            showCount={showLikesCount}
          />
        )}
      </BlockInViewOnlyMode>
      {is_comments_enabled && (
        <CommentButton
          comments_count={comments_count}
          post={post}
          className={commentButtonClassName}
          iconClassName={commentIconClassName}
          showCount={showCommentsCount}
          onCommentsClick={onCommentsClick}
        />
      )}
    </div>
  );
};
